import { observer } from 'mobx-react';
import React from 'react';

import { GridLegacy as Grid } from '@mui/material';

import EditIcon from '@mui/icons-material/Edit';

import { Button } from 'components/Button';

import { SectionGrid } from 'components/BackgroundInformation/styles';

import { COUNTRY_NAME_BY_CODE } from 'components/CountrySelect';

import { UnresolvedIdpValue } from 'utils/api/modules/user/user.types';

import { SLASH_FULL_DATE_FORMAT, UserRole } from 'core/constants';

import { useStore } from 'utils/hooks/store';
import { formatFloating } from 'utils/formatters/timezone';

import useMappedFields from '../../hooks/useMappedFields';
import { IDP_FIELDS, IdpField, UnresolvedValue } from '../IdpManaged';
import { StyledRowTitle, RowValue, TabTitle } from '../PersonalInformation/styles';
import { CardHeaderRow } from './styles';
import UserJobInformationEdit from './UserJobInformationEdit';

const NOT_SET = 'Not set';

const Row: React.FC<{
  name: string;
  value: string;
  id?: string;
  field?: IdpField;
  unresolved?: UnresolvedIdpValue;
}> = ({ name, value, id, field, unresolved }) => (
  <>
    <SectionGrid item container xs={2} alignContent="flex-start" id={id} margin="8px 0">
      <StyledRowTitle>{name}</StyledRowTitle>
    </SectionGrid>
    <SectionGrid item container xs={10}>
      <RowValue>{value || NOT_SET}</RowValue>
      {field && <UnresolvedValue field={field} unresolved={unresolved} />}
    </SectionGrid>
  </>
);

export const UserJobInformation: React.FunctionComponent = () => {
  const {
    userDetails: { details },
    session,
  } = useStore();

  const [editing, setEditing] = React.useState(false);
  const { isMapped, unresolvedFor } = useMappedFields();

  if (!details) {
    return null;
  }

  const { attributes } = details;

  const canEdit = session.user?.role === UserRole.AccountManager || session.user?.role === UserRole.Admin;
  const showObjectManager = !!details.hasObjectManager;
  const showSite = (session.user?.organisation?.maximumSitesAllowed ?? 0) > 0;

  return (
    <>
      <CardHeaderRow>
        <TabTitle>Employment &amp; organisation</TabTitle>
        {!editing && canEdit && (
          <Button size="medium" variant="text" onClick={() => setEditing(true)} disabled={!details.isActive}>
            <EditIcon sx={{ width: 20, height: 20, marginRight: '8px' }} />
            Edit
          </Button>
        )}
      </CardHeaderRow>
      {editing ? (
        <UserJobInformationEdit setEditing={setEditing} />
      ) : (
        <Grid container marginTop="16px" sx={{ padding: '0px 20px 20px 20px' }} alignItems="center">
          <Row
            field={IDP_FIELDS.jobTitle}
            unresolved={unresolvedFor(IDP_FIELDS.jobTitle)}
            name="Job title"
            value={attributes?.jobTitle ?? ''}
          />
          <Row
            field={IDP_FIELDS.department}
            unresolved={unresolvedFor(IDP_FIELDS.department)}
            name="Department"
            value={attributes?.department?.name ?? ''}
          />
          <Row
            field={IDP_FIELDS.manager}
            unresolved={unresolvedFor(IDP_FIELDS.manager)}
            name="Line manager"
            value={details.manager?.name ?? ''}
          />
          <Row
            field={IDP_FIELDS.employmentType}
            unresolved={unresolvedFor(IDP_FIELDS.employmentType)}
            name="Employment type"
            value={attributes?.employmentType?.name ?? ''}
          />
          <Row
            field={IDP_FIELDS.startDate}
            unresolved={unresolvedFor(IDP_FIELDS.startDate)}
            name="Start date"
            value={formatFloating(attributes?.startDate, 'date', SLASH_FULL_DATE_FORMAT)}
          />
          <Row
            field={IDP_FIELDS.costCentre}
            unresolved={unresolvedFor(IDP_FIELDS.costCentre)}
            name="Cost centre"
            value={details.personalDetails.costCentre ?? ''}
          />
          {showSite && (
            <Row
              field={IDP_FIELDS.site}
              unresolved={unresolvedFor(IDP_FIELDS.site)}
              name="Site"
              value={attributes?.site?.name ?? ''}
            />
          )}
          {showObjectManager && (
            <Row
              field={IDP_FIELDS.region}
              unresolved={unresolvedFor(IDP_FIELDS.region)}
              name="Region"
              value={attributes?.region?.name ?? ''}
            />
          )}
          <Row
            field={IDP_FIELDS.country}
            unresolved={unresolvedFor(IDP_FIELDS.country)}
            name="Country"
            value={attributes?.country ? (COUNTRY_NAME_BY_CODE[attributes.country] ?? '') : ''}
          />
        </Grid>
      )}
    </>
  );
};

export default observer(UserJobInformation);
